import clsx from 'clsx';
import { ChangeEvent, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppID, getAppID } from '@vestico/utils';

import { Gender, HeightUnit, WeightUnit, getAgeFromBirthyear, getBirthyearFromAge, writeUserToStorage } from '@vestico/model';

import { FormValidationContext, GlobalsContext } from '../CommonAppContext';
import { EntryField, EntryFieldConfiguration } from '../components/EntryField';
import { GenderIdentityPicker } from '../components/GenderIdentityPicker';
import { useIsMounted } from '../hooks/mounted';
import { getAgeConfiguration, getHeightConfiguration, getWeightConfiguration, updateTranslationForConfiguration } from '../model/configurations';
import { StepProps } from '../model/step';
import { UserContext } from '../model/userContext';
import StepContainer from './StepContainer';
import style from './bodyInfoForm.module.css';

export const BodyInfoForm = (props: StepProps) => {
  const { user, reloadUser } = useContext(UserContext);
  const formValidationContext = useContext(FormValidationContext);
  const { stylesConfig } = useContext(GlobalsContext);

  const mounted = useIsMounted();
  const { t } = useTranslation('shared');
  const [submitting, setSubmitting] = useState(false);

  useMemo(() => {
    const isSubmittingFromExternally = props.submitting;
    if (isSubmittingFromExternally) {
      setSubmitting(isSubmittingFromExternally);
    }
    return isSubmittingFromExternally;
  }, [props.submitting]);

  const [gender, setGender] = useState(user?.gender !== undefined && user?.gender !== Gender.unset ? user.gender : Gender.female);

  const [heightConfiguration, setHeightConfiguration] = useState(getHeightConfiguration(user?.heightUnit));
  const [weightConfiguration, setWeightConfiguration] = useState(getWeightConfiguration(user?.weightUnit));
  const [ageConfiguration, setAgeConfiguration] = useState(getAgeConfiguration());

  const isFitUploader = useMemo(() => getAppID() === AppID.fitUploader, []);

  const heightError = useMemo(() => !!(mounted.current && !heightConfiguration.value), [heightConfiguration, submitting, mounted]);
  const weightError = useMemo(() => !!(mounted.current && !weightConfiguration.value), [weightConfiguration, submitting, mounted]);
  const ageError = useMemo(() => !!(mounted.current && !ageConfiguration.value), [ageConfiguration, submitting, mounted]);
  const ageMinError = useMemo(
    () => (!ageError && isFitUploader && ageConfiguration.value && ageConfiguration.value < 18 ? t('LOC_BASIC_INFO_ERROR_AGE_MIN') : undefined),
    [isFitUploader, t, ageError, ageConfiguration.value]
  );
  const formValid = useMemo(() => !heightError && !weightError && !ageError && !ageMinError, [heightError, weightError, ageError, ageMinError]);

  weightConfiguration.didUpdateConfig = useCallback((newConfig: EntryFieldConfiguration) => {
    setWeightConfiguration(newConfig);
  }, []);

  heightConfiguration.didUpdateConfig = useCallback((newConfig: EntryFieldConfiguration) => {
    setHeightConfiguration(newConfig);
  }, []);

  ageConfiguration.didUpdateConfig = useCallback((newConfig: EntryFieldConfiguration) => {
    setAgeConfiguration(newConfig);
  }, []);

  useEffect(() => {
    setWeightConfiguration(updateTranslationForConfiguration(weightConfiguration, user?.weightUnit, getWeightConfiguration));
    setHeightConfiguration(updateTranslationForConfiguration(heightConfiguration, user?.heightUnit, getHeightConfiguration));
    setAgeConfiguration(updateTranslationForConfiguration(ageConfiguration, undefined, getAgeConfiguration));
  }, [t, user?.heightUnit, user?.weightUnit]);

  useMemo(() => {
    if (!user) {
      return;
    }

    if (user.gender !== undefined && user.gender !== Gender.unset) {
      setGender(user.gender);
    }

    heightConfiguration.value = user.height;
    weightConfiguration.value = user.weight;
    ageConfiguration.value = getAgeFromBirthyear(user.birthyear);
  }, [user]);

  const updateUserProperties = useCallback(() => {
    if (!user) {
      console.error('no user fetched');
      return;
    }

    user.gender = gender;
    user.height = heightConfiguration.value;
    user.heightUnit = heightConfiguration.options[heightConfiguration.selectedOptionIndex].unitType as HeightUnit;
    user.weight = weightConfiguration.value;
    user.weightUnit = weightConfiguration.options[weightConfiguration.selectedOptionIndex].unitType as WeightUnit;
    user.birthyear = getBirthyearFromAge(ageConfiguration.value);

    if (!user.displayName && !user.username && isFitUploader) {
      user.displayName = user.fallbackUsername;
    }
  }, [user, gender, heightConfiguration, weightConfiguration, ageConfiguration, isFitUploader]);

  const updateGender = useCallback(
    (newGender: Gender) => {
      if (!user) {
        return;
      }

      updateUserProperties();
      if (newGender !== undefined && newGender !== Gender.unset) {
        setGender(newGender);
      }

      // manually set user properties here with new gender as the local state hasn't updated yet
      user.gender = newGender;

      writeUserToStorage(user);

      if (reloadUser) {
        reloadUser(false);
      }
    },
    [user, updateUserProperties, reloadUser]
  );

  useLayoutEffect(() => {
    if (!submitting) {
      return;
    }

    formValidationContext.updateStep(props.path, formValid);

    heightConfiguration.showErrorIfNeeded = true;
    weightConfiguration.showErrorIfNeeded = true;
    ageConfiguration.showErrorIfNeeded = true;

    if (!formValid) {
      setSubmitting(false);
      return;
    }

    if (!user) {
      console.error('missing user');
      return;
    }

    // store data
    updateUserProperties();
    writeUserToStorage(user);
    heightConfiguration.showErrorIfNeeded = false;
    weightConfiguration.showErrorIfNeeded = false;
    ageConfiguration.showErrorIfNeeded = false;
  }, [formValid, submitting]);

  const weightHelpText = useMemo(() => {
    return isFitUploader ? (
      <div>
        <p style={{ fontWeight: 'bolder', marginTop: 0 }}>{t('LOC_HELP_TEXT_WEIGHT_TITLE')}</p>
        <p>{t('LOC_HELP_TEXT_WEIGHT_TEXT_1')}</p>
        <p style={{ marginBottom: 0 }}>{t('LOC_HELP_TEXT_WEIGHT_TEXT_2')}</p>
      </div>
    ) : undefined;
  }, [isFitUploader, t]);

  const onFormSubmit = useCallback((e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
  }, []);

  return (
    <StepContainer title={props.title}>
      <form
        className={clsx(style.formLayout)}
        onSubmit={onFormSubmit}
        // skipcq JS-0455
        // eslint-disable-next-line react/no-unknown-property
        body-scroll-lock-ignore="true"
      >
        {!stylesConfig.stylesConfig.hideGenderSelection ? <GenderIdentityPicker gender={gender} didUpdateGender={updateGender} /> : null}
        <EntryField configuration={heightConfiguration} />
        <EntryField configuration={weightConfiguration} helpText={weightHelpText} />
        <EntryField configuration={ageConfiguration} additionalError={ageMinError} />
      </form>
    </StepContainer>
  );
};

export default BodyInfoForm;
